import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatCurrency, relTime } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { DEAL } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, PhoneCall, StickyNote } from "lucide-react";
import TasksTimeline from "@/components/TasksTimeline";
import DealCostSummary from "@/components/DealCostSummary";

export default function DealDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: deal, isLoading } = useQuery({ queryKey: ["deal", id], queryFn: () => api.get(`/deals/${id}`).then((r) => r.data) });
  const { data: stages = [] } = useQuery({ queryKey: ["stages"], queryFn: () => api.get("/pipeline/stages").then((r) => r.data) });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/users").then((r) => r.data).catch(() => []) });
  const [note, setNote] = useState("");

  const move = useMutation({
    mutationFn: (stageId) => api.post(`/deals/${id}/move`, { stage_id: stageId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deal", id] }); toast.success("Stage updated"); },
  });

  const addActivity = useMutation({
    mutationFn: (payload) => api.post(`/deals/${id}/activities`, payload),
    onSuccess: () => { setNote(""); qc.invalidateQueries({ queryKey: ["deal", id] }); },
  });

  if (isLoading || !deal) return <div className="p-10 text-muted-foreground">Loading…</div>;
  const currentStage = stages.find((s) => s.id === deal.stage_id);

  return (
    <div>
      <PageHeader
        overline={<Link to="/pipeline" className="hover:text-primary flex items-center gap-1"><ArrowLeft className="h-3 w-3" />Pipeline</Link>}
        title={deal.name}
        subtitle={`${deal.contact?.name || ""}${deal.contact?.company_name ? " · " + deal.contact.company_name : ""}`}
        actions={
          <Select value={deal.stage_id} onValueChange={(v) => move.mutate(v)}>
            <SelectTrigger data-testid={DEAL.moveStage} className="rounded-sm w-48 bg-primary/10 border-primary/40"><SelectValue /></SelectTrigger>
            <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />

      <div className="px-6 lg:px-10 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MiniStat label="Value" value={formatCurrency(deal.value, deal.currency)} highlight />
            <MiniStat label="Stage" value={currentStage?.name || "-"} />
            <MiniStat label="Close date" value={deal.expected_close_date || "—"} />
            <MiniStat label="Owner" value={deal.assigned_agent?.name || "Unassigned"} />
          </div>

          <Card className="rounded-sm bg-card border-border p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Tags</div>
            <div className="flex flex-wrap gap-1.5">
              {(deal.tags || []).map((t) => <Badge key={t} variant="secondary" className="rounded-sm">{t}</Badge>)}
              {(deal.tags || []).length === 0 && <div className="text-sm text-muted-foreground">No tags yet</div>}
            </div>
          </Card>

          <TasksTimeline dealId={id} teamMembers={team} />

          <DealCostSummary dealId={id} />

          <Card className="rounded-sm bg-card border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <StickyNote className="h-4 w-4 text-primary" />
              <div className="font-display text-xl font-extrabold">Activity Timeline</div>
            </div>
            <div className="flex gap-2 mb-4">
              <Textarea rows={2} className="rounded-sm bg-secondary/40" placeholder="Add a note, call summary, email…" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button data-testid={DEAL.addActivity} disabled={!note.trim() || addActivity.isPending} onClick={() => addActivity.mutate({ text: note, type: "note" })} className="rounded-sm bg-primary self-end">Add</Button>
            </div>
            <div className="space-y-3">
              {(deal.activities || []).map((a) => (
                <div key={a.id} className="border-l-2 border-primary/40 pl-4 py-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.type} · {relTime(a.created_at)} · {a.user_name}</div>
                  <div className="text-sm mt-0.5">{a.text}</div>
                </div>
              ))}
              {(deal.activities || []).length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No activities yet</div>}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="rounded-sm bg-card border-border p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Contact</div>
            <div className="font-semibold text-lg">{deal.contact?.name}</div>
            <div className="text-sm text-muted-foreground">{deal.contact?.email}</div>
            <div className="text-sm text-muted-foreground">{deal.contact?.phone}</div>
            {deal.contact?.company_name && <div className="text-sm text-muted-foreground mt-1">{deal.contact.company_name}</div>}
          </Card>

          <Card className="rounded-sm bg-card border-border p-6">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-4 w-4 text-primary" />
              <div className="font-display text-lg font-extrabold">Original Conversation</div>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(deal.messages || []).map((m) => (
                <div key={m.id} className={`p-2 rounded-sm text-sm ${m.sender_type === "customer" ? "bg-secondary/60" : "bg-primary/20"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{m.sender_name}</div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
              {(deal.messages || []).length === 0 && <div className="text-sm text-muted-foreground">No linked conversation</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <Card className={`rounded-sm p-4 border-border ${highlight ? "bg-primary/10 border-primary/40" : "bg-card"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      <div className="font-display text-lg font-extrabold mt-1 truncate">{value}</div>
    </Card>
  );
}
